'use client';

import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import Image from '@/helpers/Image';

import reviewStars from '@/public/text-only-crypto/review-stars.svg';
import { PageTextOnlyCryptoSchema } from '@/sanity/pages/PageTextOnlyCryptoTemplate';

/** Figma: desktop 7205:2468, mobile 8712:29230. */
const COLUMN_WIDTHS = ['24.5rem', '16.875rem', '14.375rem', '14.375rem'];

const MOBILE_LABEL_WIDTHS = [
  'max-lg:max-w-[18.625rem]',
  'max-lg:max-w-[15.625rem]',
  'max-lg:max-w-[16.625rem]',
  '',
];

export default function ReviewStatsSection({
  data,
  className,
}: {
  data: PageTextOnlyCryptoSchema;
  className?: string;
}) {
  const stats = data?.reviewStats ?? [];
  if (!data?.reviewsTitle && stats.length === 0) return null;

  return (
    <section
      className={cn(
        'relative flex flex-col items-center justify-center gap-[5rem] w-full bg-white px-[2rem] lg:px-0 max-lg:py-[5rem] lg:py-[12.5rem]',
        className,
      )}
    >
      <div className="flex flex-col items-center justify-center max-lg:gap-[1rem] lg:gap-[1.5rem]">
        <Image
          src={reviewStars}
          alt=""
          aria-hidden
          className="!w-[15.25rem] !h-[2.859rem] shrink-0"
        />
        {data?.reviewsTitle && (
          <p className="mb-0 max-lg:w-[min(30.5rem,100%)] font-geist font-medium text-[3.5rem] leading-[1.3] tracking-[-0.175rem] text-black text-center">
            {data.reviewsTitle}
          </p>
        )}
      </div>

      {stats.length > 0 && (
        <div className="relative flex flex-wrap items-stretch justify-center w-[min(96rem,100%)] lg:gap-x-[4rem] lg:gap-y-[5.25rem] lg:h-[13.375rem]">
          {stats.length > 1 && (
            <span
              aria-hidden
              className="lg:hidden absolute left-1/2 top-0 bottom-[-1.9375rem] w-px bg-black/10"
            />
          )}
          {stats.map((stat, index) => (
            <Fragment key={stat?._key ?? index}>
              {index > 0 && (
                <span
                  aria-hidden
                  className="hidden lg:block self-stretch w-px bg-black/10"
                />
              )}
              <div
                className={cn(
                  'flex flex-col max-lg:w-1/2 max-lg:!max-w-none max-lg:gap-[3rem] max-lg:p-[2.5rem] max-lg:pb-[1.5rem] lg:justify-between lg:gap-[1rem] lg:w-auto',
                  index < 2 ? 'max-lg:min-h-[19.125rem]' : 'max-lg:min-h-[16rem]',
                )}
                style={{ maxWidth: COLUMN_WIDTHS[index] ?? COLUMN_WIDTHS[1] }}
              >
                {stat?.value && (
                  <p className="mb-0 font-geist font-medium text-[2.75rem] max-lg:leading-[normal] lg:leading-normal tracking-[-0.1375rem] text-[#003828] whitespace-nowrap">
                    {stat.value}
                  </p>
                )}
                {stat?.label && (
                  <p
                    className={cn(
                      'mb-0 font-geist font-normal text-[1.75rem] leading-[1.5] text-black opacity-60',
                      'max-lg:[text-wrap:wrap]',
                      MOBILE_LABEL_WIDTHS[index],
                    )}
                  >
                    {stat.label}
                  </p>
                )}
              </div>
              {index % 2 === 1 && index < stats.length - 1 && (
                <span aria-hidden className="lg:hidden relative w-full h-0">
                  <span className="absolute left-[2.875rem] right-0 top-[0.45rem] h-px bg-black/10" />
                </span>
              )}
            </Fragment>
          ))}
        </div>
      )}
    </section>
  );
}
